import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { getMapboxAccessToken } from '../../constants';
import { colors } from '../../theme/colors';

// Sydney CBD default camera before any feature exists.
const DEFAULT_CENTER = [151.2093, -33.8688];
const GEOCODE = 'https://api.mapbox.com/geocoding/v5/mapbox.places';
const POINT_COLOR = colors.accent;
const ROUTE_COLOR = '#16a34a';
const EMPTY_FC = { type: 'FeatureCollection', features: [] };

const Wrap = styled.div``;
const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
`;
const ModeBtn = styled.button`
  border: 1px solid ${(p) => (p.$active ? colors.accent : colors.border)};
  background: ${(p) => (p.$active ? colors.accentSoft : '#fff')};
  color: ${(p) => (p.$active ? colors.accent : colors.ink)};
  font-weight: 700;
  font-size: 13px;
  border-radius: 999px;
  padding: 6px 12px;
  cursor: pointer;
`;
const SearchRow = styled.div`
  position: relative;
  flex: 1;
  min-width: 200px;
`;
const SearchInput = styled.input`
  width: 100%;
  box-sizing: border-box;
`;
const Suggestions = styled.ul`
  position: absolute;
  z-index: 30;
  top: calc(100% + 2px);
  left: 0;
  right: 0;
  margin: 0;
  padding: 4px;
  list-style: none;
  background: #fff;
  border: 1px solid ${colors.border};
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  max-height: 240px;
  overflow-y: auto;
`;
const Suggestion = styled.li`
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 13px;
  cursor: pointer;
  &:hover {
    background: ${colors.accentSoft};
  }
`;
const MapMount = styled.div`
  height: 360px;
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid ${colors.border};
  .mapboxgl-ctrl-logo,
  .mapboxgl-ctrl-attrib {
    display: none !important;
  }
`;
const Hint = styled.div`
  font-size: 12px;
  color: ${colors.inkMuted};
  margin: 6px 0 10px;
`;
const Lists = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;
const Panel = styled.div`
  border: 1px solid ${colors.border};
  border-radius: 12px;
  padding: 10px 12px;
  background: ${colors.bg};
`;
const PanelTitle = styled.div`
  font-size: 13px;
  font-weight: 800;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
`;
const Dot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${(p) => p.$color};
  display: inline-block;
`;
const ItemRow = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  margin-bottom: 6px;
  input {
    flex: 1;
    min-width: 0;
  }
`;
const Small = styled.span`
  font-size: 11px;
  color: ${colors.inkMuted};
  white-space: nowrap;
`;
const RemoveX = styled.button`
  background: none;
  border: none;
  color: #b91c1c;
  font-weight: 800;
  font-size: 13px;
  cursor: pointer;
  padding: 0 4px;
`;
const RouteCard = styled.div`
  border: 1px solid ${(p) => (p.$active ? ROUTE_COLOR : colors.border)};
  border-radius: 10px;
  padding: 8px;
  margin-bottom: 8px;
  background: #fff;
`;
const MiniBtn = styled.button`
  border: 1px solid ${colors.border};
  background: #fff;
  font-size: 12px;
  font-weight: 700;
  border-radius: 8px;
  padding: 4px 8px;
  cursor: pointer;
`;
const MainVenueBtn = styled.button`
  border: 1px solid ${(p) => (p.$active ? colors.accent : colors.border)};
  background: ${(p) => (p.$active ? colors.accent : '#fff')};
  color: ${(p) => (p.$active ? '#fff' : colors.inkMuted)};
  font-size: 11px;
  font-weight: 800;
  border-radius: 999px;
  padding: 3px 9px;
  cursor: pointer;
  white-space: nowrap;
  &:disabled { cursor: default; opacity: ${(p) => (p.$active ? 1 : 0.5)}; }
`;
const Fallback = styled.div`
  font-size: 13px;
  color: ${colors.inkMuted};
`;

async function reverseGeocode(lng, lat, token) {
  try {
    const res = await fetch(`${GEOCODE}/${lng},${lat}.json?access_token=${token}&limit=1&language=zh,en`);
    const data = await res.json();
    return data?.features?.[0]?.place_name || '';
  } catch {
    return '';
  }
}

function makeWaypointEl(n) {
  const el = document.createElement('div');
  el.style.cssText =
    'width:22px;height:22px;border-radius:50%;background:#16a34a;color:#fff;font-size:12px;font-weight:700;display:flex;align-items:center;justify-content:center;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.3);cursor:pointer;';
  el.textContent = String(n);
  return el;
}

const uid = (p) => `${p}_${Date.now().toString(36)}${Math.floor(Math.random() * 1e4).toString(36)}`;

/**
 * Multi-point + multi-route geography editor for an activity.
 *
 * `value` = { locations: [{ id, name, lat, lng }],
 *             routes:    [{ id, name, waypoints: [{ name, lat, lng }] }] }
 * Consistent with the app's route logic (pin points, connect into a line);
 * points and routes can be combined freely.
 */
export default function GeoDesigner({ value, onChange, disabled }) {
  const token = getMapboxAccessToken();
  const mountRef = useRef(null);
  const mapRef = useRef(null);
  const readyRef = useRef(false);
  const centeredRef = useRef(false);
  const markersRef = useRef([]);
  const myLocRef = useRef(null);
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  const modeRef = useRef('point');
  const activeRouteRef = useRef(null);
  const disabledRef = useRef(disabled);
  valueRef.current = value;
  onChangeRef.current = onChange;
  disabledRef.current = disabled;

  const [mode, setMode] = useState('point');
  const [activeRouteId, setActiveRouteId] = useState(null);
  const [ready, setReady] = useState(false);
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const debRef = useRef(null);
  modeRef.current = mode;
  activeRouteRef.current = activeRouteId;

  const locations = value?.locations || [];
  const routes = value?.routes || [];

  const emit = useCallback((next) => onChangeRef.current(next), []);

  // --- mutations (stable, read latest via valueRef) -------------------------
  const addPointAt = useCallback(async (lat, lng, name) => {
    const id = uid('loc');
    const cur = valueRef.current || {};
    emit({ ...cur, locations: [...(cur.locations || []), { id, name: name || '', lat, lng }] });
    if (!name && token) {
      const resolved = await reverseGeocode(lng, lat, token);
      if (resolved) {
        const c = valueRef.current || {};
        emit({ ...c, locations: (c.locations || []).map((p) => (p.id === id ? { ...p, name: resolved } : p)) });
      }
    }
  }, [emit, token]);

  const updatePoint = useCallback((id, patch) => {
    const cur = valueRef.current || {};
    emit({ ...cur, locations: (cur.locations || []).map((p) => (p.id === id ? { ...p, ...patch } : p)) });
  }, [emit]);

  const removePoint = useCallback((id) => {
    const cur = valueRef.current || {};
    emit({ ...cur, locations: (cur.locations || []).filter((p) => p.id !== id) });
  }, [emit]);

  // 主会场 (main venue): exactly one location can be the map anchor shown in the
  // app. Toggling one clears the flag on the others; toggling the current one off
  // leaves no main venue (anchor falls back to the first location).
  const setMainVenue = useCallback((id) => {
    const cur = valueRef.current || {};
    const list = cur.locations || [];
    const already = list.find((p) => p.id === id)?.isMainVenue === true;
    emit({ ...cur, locations: list.map((p) => ({ ...p, isMainVenue: already ? false : p.id === id })) });
  }, [emit]);

  const addWaypointAt = useCallback((routeId, lat, lng, name) => {
    const cur = valueRef.current || {};
    emit({
      ...cur,
      routes: (cur.routes || []).map((r) =>
        r.id === routeId ? { ...r, waypoints: [...(r.waypoints || []), { name: name || '', lat, lng }] } : r),
    });
  }, [emit]);

  const updateWaypoint = useCallback((routeId, index, patch) => {
    const cur = valueRef.current || {};
    emit({
      ...cur,
      routes: (cur.routes || []).map((r) =>
        r.id === routeId ? { ...r, waypoints: r.waypoints.map((w, i) => (i === index ? { ...w, ...patch } : w)) } : r),
    });
  }, [emit]);

  const removeWaypoint = useCallback((routeId, index) => {
    const cur = valueRef.current || {};
    emit({
      ...cur,
      routes: (cur.routes || []).map((r) =>
        r.id === routeId ? { ...r, waypoints: r.waypoints.filter((_, i) => i !== index) } : r),
    });
  }, [emit]);

  const setRouteName = useCallback((routeId, name) => {
    const cur = valueRef.current || {};
    emit({ ...cur, routes: (cur.routes || []).map((r) => (r.id === routeId ? { ...r, name } : r)) });
  }, [emit]);

  const removeRoute = useCallback((routeId) => {
    const cur = valueRef.current || {};
    emit({ ...cur, routes: (cur.routes || []).filter((r) => r.id !== routeId) });
  }, [emit]);

  const addRoute = () => {
    const id = uid('route');
    const cur = valueRef.current || {};
    emit({ ...cur, routes: [...(cur.routes || []), { id, name: '', waypoints: [] }] });
    setMode('route');
    setActiveRouteId(id);
  };

  // --- browser geolocation (center map on the organizer's current position) --
  const [locating, setLocating] = useState(false);
  const locateMe = useCallback((auto) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) return;
    if (!auto) setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        const map = mapRef.current;
        if (!map) return;
        const { longitude, latitude } = pos.coords;
        if (!myLocRef.current) {
          const el = document.createElement('div');
          el.style.cssText =
            'width:16px;height:16px;border-radius:50%;background:#2563eb;border:3px solid #fff;box-shadow:0 0 0 4px rgba(37,99,235,.3);';
          myLocRef.current = new mapboxgl.Marker({ element: el }).setLngLat([longitude, latitude]).addTo(map);
        } else {
          myLocRef.current.setLngLat([longitude, latitude]);
        }
        // Auto-locate only recenters when nothing has framed the map yet.
        if (!auto || !centeredRef.current) {
          map.flyTo({ center: [longitude, latitude], zoom: 15 });
          centeredRef.current = true;
        }
      },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 },
    );
  }, []);

  // --- forward geocoding search ---------------------------------------------
  useEffect(() => {
    if (!token) return undefined;
    const q = query.trim();
    if (q.length < 2) {
      setSuggestions([]);
      return undefined;
    }
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${GEOCODE}/${encodeURIComponent(q)}.json?access_token=${token}&limit=5&language=zh,en`);
        const data = await res.json();
        setSuggestions(Array.isArray(data?.features) ? data.features : []);
        setOpen(true);
      } catch {
        setSuggestions([]);
      }
    }, 350);
    return () => debRef.current && clearTimeout(debRef.current);
  }, [query, token]);

  const handleSelectFeature = (f) => {
    const [lng, lat] = f.center;
    if (modeRef.current === 'route' && activeRouteRef.current) {
      addWaypointAt(activeRouteRef.current, lat, lng, f.text || '');
    } else {
      addPointAt(lat, lng, f.place_name);
    }
    if (mapRef.current) mapRef.current.flyTo({ center: [lng, lat], zoom: 15 });
    setSuggestions([]);
    setOpen(false);
    setQuery('');
  };

  // --- map init (once) ------------------------------------------------------
  useEffect(() => {
    if (!token || !mountRef.current || mapRef.current) return undefined;
    mapboxgl.accessToken = token;
    const map = new mapboxgl.Map({
      container: mountRef.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: DEFAULT_CENTER,
      zoom: 9,
      attributionControl: false,
    });
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
    mapRef.current = map;

    map.on('load', () => {
      map.addSource('geo-routes', { type: 'geojson', data: EMPTY_FC });
      map.addLayer({
        id: 'geo-routes-line',
        type: 'line',
        source: 'geo-routes',
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': ROUTE_COLOR, 'line-width': 3, 'line-opacity': 0.85 },
      });
      readyRef.current = true;
      setReady(true);
      map.resize();
      // New activity (no features yet) → jump to the organizer's location.
      const v = valueRef.current || {};
      const featureCount = (v.locations?.length || 0) + (v.routes || []).reduce((n, r) => n + (r.waypoints?.length || 0), 0);
      if (featureCount === 0) locateMe(true);
    });

    map.on('click', (e) => {
      if (disabledRef.current) return;
      if (modeRef.current === 'route' && activeRouteRef.current) {
        addWaypointAt(activeRouteRef.current, e.lngLat.lat, e.lngLat.lng);
      } else {
        addPointAt(e.lngLat.lat, e.lngLat.lng);
      }
    });

    return () => {
      if (myLocRef.current) { myLocRef.current.remove(); myLocRef.current = null; }
      map.remove();
      mapRef.current = null;
      readyRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // --- render markers + route lines whenever the value changes --------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;

    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    (value?.locations || []).forEach((p) => {
      if (!Number.isFinite(p.lat) || !Number.isFinite(p.lng)) return;
      const marker = new mapboxgl.Marker({ draggable: !disabled, color: POINT_COLOR })
        .setLngLat([p.lng, p.lat])
        .addTo(map);
      marker.on('dragend', () => {
        const ll = marker.getLngLat();
        updatePoint(p.id, { lat: ll.lat, lng: ll.lng });
      });
      markersRef.current.push(marker);
    });

    (value?.routes || []).forEach((r) => {
      (r.waypoints || []).forEach((w, i) => {
        if (!Number.isFinite(w.lat) || !Number.isFinite(w.lng)) return;
        const marker = new mapboxgl.Marker({ element: makeWaypointEl(i + 1), draggable: !disabled })
          .setLngLat([w.lng, w.lat])
          .addTo(map);
        marker.on('dragend', () => {
          const ll = marker.getLngLat();
          updateWaypoint(r.id, i, { lat: ll.lat, lng: ll.lng });
        });
        markersRef.current.push(marker);
      });
    });

    const src = map.getSource('geo-routes');
    if (src) {
      src.setData({
        type: 'FeatureCollection',
        features: (value?.routes || [])
          .filter((r) => (r.waypoints || []).filter((w) => Number.isFinite(w.lat) && Number.isFinite(w.lng)).length >= 2)
          .map((r) => ({
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: r.waypoints
                .filter((w) => Number.isFinite(w.lat) && Number.isFinite(w.lng))
                .map((w) => [w.lng, w.lat]),
            },
          })),
      });
    }

    // Fit to all features on first population.
    if (!centeredRef.current) {
      const coords = [];
      (value?.locations || []).forEach((p) => Number.isFinite(p.lat) && coords.push([p.lng, p.lat]));
      (value?.routes || []).forEach((r) => (r.waypoints || []).forEach((w) => Number.isFinite(w.lat) && coords.push([w.lng, w.lat])));
      if (coords.length === 1) {
        map.flyTo({ center: coords[0], zoom: 14 });
        centeredRef.current = true;
      } else if (coords.length > 1) {
        const b = coords.reduce((bb, c) => bb.extend(c), new mapboxgl.LngLatBounds(coords[0], coords[0]));
        map.fitBounds(b, { padding: 60, maxZoom: 15, duration: 0 });
        centeredRef.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, ready, disabled]);

  if (!token) {
    return <Fallback>未配置 Mapbox token，无法加载地图选点。请设置 REACT_APP_MAPBOX_ACCESS_TOKEN。</Fallback>;
  }

  const routeMode = mode === 'route' && activeRouteId;

  return (
    <Wrap>
      <Toolbar>
        <ModeBtn
          type="button"
          $active={mode === 'point'}
          disabled={disabled}
          onClick={() => { setMode('point'); setActiveRouteId(null); }}
        >
          📍 添加地点
        </ModeBtn>
        <ModeBtn type="button" disabled={disabled} onClick={addRoute}>＋ 新建路线</ModeBtn>
        <ModeBtn type="button" onClick={() => locateMe(false)} title="定位到我的当前位置">
          {locating ? '定位中…' : '🎯 我的位置'}
        </ModeBtn>
        <SearchRow>
          <SearchInput
            value={query}
            disabled={disabled}
            placeholder="搜索地址跳转 / 直接添加到当前模式…"
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => suggestions.length && setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
          />
          {open && suggestions.length ? (
            <Suggestions>
              {suggestions.map((f) => (
                <Suggestion key={f.id} onMouseDown={() => handleSelectFeature(f)}>{f.place_name}</Suggestion>
              ))}
            </Suggestions>
          ) : null}
        </SearchRow>
      </Toolbar>

      <MapMount ref={mountRef} />
      <Hint>
        {disabled
          ? '已发布，地点/路线只读。'
          : routeMode
            ? '路线模式：点击地图依次添加途经点，连成一条线；拖动绿色圆点微调。'
            : '地点模式：点击地图放置地点图钉；拖动图钉微调。可添加多个地点，或新建一条/多条路线。'}
      </Hint>

      <Lists>
        <Panel>
          <PanelTitle><Dot $color={POINT_COLOR} /> 地点 · {locations.length}</PanelTitle>
          {locations.length === 0 ? <Small>还没有地点。选「添加地点」后点击地图。</Small> : null}
          {locations.map((p) => (
            <ItemRow key={p.id}>
              <input
                value={p.name}
                disabled={disabled}
                placeholder="地点名称"
                onChange={(e) => updatePoint(p.id, { name: e.target.value })}
              />
              <Small>{Number.isFinite(p.lat) ? `${p.lat.toFixed(4)}, ${p.lng.toFixed(4)}` : '—'}</Small>
              <MainVenueBtn
                type="button"
                $active={p.isMainVenue === true}
                disabled={disabled}
                title={p.isMainVenue ? '主会场（活动在地图上的位置）' : '设为主会场'}
                onClick={() => setMainVenue(p.id)}
              >
                {p.isMainVenue ? '★ 主会场' : '☆ 设为主会场'}
              </MainVenueBtn>
              {!disabled ? <RemoveX type="button" onClick={() => removePoint(p.id)}>✕</RemoveX> : null}
            </ItemRow>
          ))}
        </Panel>

        <Panel>
          <PanelTitle><Dot $color={ROUTE_COLOR} /> 路线 · {routes.length}</PanelTitle>
          {routes.length === 0 ? <Small>还没有路线。点「＋ 新建路线」后在地图上连点成线。</Small> : null}
          {routes.map((r) => {
            const active = activeRouteId === r.id && mode === 'route';
            return (
              <RouteCard key={r.id} $active={active}>
                <ItemRow>
                  <input
                    value={r.name}
                    disabled={disabled}
                    placeholder="路线名称"
                    onChange={(e) => setRouteName(r.id, e.target.value)}
                  />
                  <Small>{(r.waypoints || []).length} 点</Small>
                  {!disabled ? <RemoveX type="button" onClick={() => removeRoute(r.id)}>✕</RemoveX> : null}
                </ItemRow>
                {!disabled ? (
                  <div style={{ display: 'flex', gap: 6, marginBottom: active ? 8 : 0 }}>
                    {active ? (
                      <MiniBtn type="button" onClick={() => { setMode('point'); setActiveRouteId(null); }}>✓ 完成编辑</MiniBtn>
                    ) : (
                      <MiniBtn type="button" onClick={() => { setMode('route'); setActiveRouteId(r.id); }}>✎ 编辑此路线</MiniBtn>
                    )}
                  </div>
                ) : null}
                {active && (r.waypoints || []).length > 0 ? (
                  <div>
                    {r.waypoints.map((w, i) => (
                      <ItemRow key={i}>
                        <Small>{i + 1}.</Small>
                        <input
                          value={w.name || ''}
                          placeholder={`途经点 ${i + 1}`}
                          onChange={(e) => updateWaypoint(r.id, i, { name: e.target.value })}
                        />
                        <RemoveX type="button" onClick={() => removeWaypoint(r.id, i)}>✕</RemoveX>
                      </ItemRow>
                    ))}
                  </div>
                ) : null}
              </RouteCard>
            );
          })}
        </Panel>
      </Lists>
    </Wrap>
  );
}
